import { roundOre } from './money.js';
/**
 * Future value of a bucket: opening value PV growing at annual rate g with an
 * annual contribution C, over t years (ordinary annuity):
 *   FV(t) = PV·(1+g)^t + C·[((1+g)^t − 1) / g]
 * Reference: finance-app/danish-personal-finance-rules §3, spec §5.
 */
export function futureValue(pv, annualGrowth, annualContribution, years) {
    if (years < 0)
        throw new Error('years must be >= 0');
    const g = annualGrowth;
    const f = Math.pow(1 + g, years);
    const contribFactor = g === 0 ? years : (f - 1) / g;
    return roundOre(pv * f + annualContribution * contribFactor);
}
/** Convert a nominal return to a real (inflation-adjusted) return: (1+n)/(1+i) − 1. */
export function realReturn(nominal, inflation) {
    return (1 + nominal) / (1 + inflation) - 1;
}
/**
 * FIRE number = annual living expenses ÷ safe withdrawal rate.
 * 4% → ×25, 3.5% → ×28.6, 3% → ×33.3. Reference: danish-rules §3 (Trinity/4% rule).
 */
export function fireNumber(annualExpenses, withdrawalRate = 0.04) {
    if (withdrawalRate <= 0)
        throw new Error('withdrawalRate must be > 0');
    return roundOre(annualExpenses / withdrawalRate);
}
/**
 * Required capital to retire = annual **retirement** spending ÷ safe withdrawal rate.
 * The 4%-rule conversion (danish-rules §3) applied to a dedicated retirement budget rather than
 * the current budget: retirement spending differs from current spending. Same math as
 * {@link fireNumber}, named for the Projection module's retirement-budget-driven capital target.
 */
export function requiredCapital(annualRetirementSpending, withdrawalRate = 0.04) {
    if (withdrawalRate <= 0)
        throw new Error('withdrawalRate must be > 0');
    return roundOre(annualRetirementSpending / withdrawalRate);
}
function returnFor(schedule, yearIndex) {
    if (schedule.length === 0)
        return 0;
    return schedule[Math.min(yearIndex, schedule.length - 1)];
}
/**
 * Project total wealth year-by-year (spec §5 FV + §3.3):
 *   value_y = value_{y-1}·(1 + r_y·(1 − taxDrag)) + contribution
 * summed across buckets, plus a linear pension/equity column. Year 0 is the opening
 * snapshot (age startAge); the series runs to startAge + years.
 */
export function projectWealth(input) {
    const values = input.buckets.map((b) => b.openingValue);
    const points = [];
    const infl = input.inflation ?? 0;
    for (let y = 0; y <= input.years; y++) {
        if (y > 0) {
            input.buckets.forEach((b, i) => {
                const r = returnFor(b.returnSchedule, y - 1);
                const effective = r * (1 - (b.taxDrag ?? 0));
                values[i] = roundOre(values[i] * (1 + effective) + b.annualContribution);
            });
        }
        const bucketsTotal = values.reduce((s, v) => s + v, 0);
        const pension = roundOre((input.pensionOpening ?? 0) + (input.pensionAnnualAdd ?? 0) * y);
        let netWorth = bucketsTotal + pension;
        let b = bucketsTotal;
        let p = pension;
        if (input.real && infl > 0) {
            const deflator = Math.pow(1 + infl, y);
            netWorth = roundOre(netWorth / deflator);
            b = roundOre(b / deflator);
            p = roundOre(p / deflator);
        }
        points.push({ year: input.startYear + y, age: input.startAge + y, netWorth, buckets: b, pension: p });
    }
    return points;
}
/**
 * Given a projected net-worth series (per year, ascending) and the FIRE target,
 * find the first year net worth crosses it — "retirement possible at age X".
 */
export function fireCrossover(series, target) {
    const crossover = series.find((p) => p.netWorth >= target) ?? null;
    return { fireNumber: target, crossover };
}
//# sourceMappingURL=projection.js.map